import React, {
  useEffect,
  useState,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { useWindowSize } from '@hooks/useWindowSize';
import {
  Button,
  Flex,
  Header,
  GoBackButton, CheckoutBlock,
} from '@components';
import { routes } from '@utils';
import { STORE_NAMES } from '@constants';

import { getAllSubscriptions, resetUserActiveOrderState, resetUserPaymentFlowState } from 'src/store/products/duck';
import { trackGTMViewItem } from '@utils/helpers';
import { SubscriptionBlock } from './components';
import { formatSubscriptionTitle, getSubscription } from '../../utils/helpers';
import {
  getContainerStyles,
  getContentContainerStyles,
  getGoBackButtonStyles,
  styles,
} from './styles';
import { PAGE_STATE } from './layoutProps';

const Subscription = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [pageState, setPageState] = useState(PAGE_STATE.ALL);
  const { id } = useParams();

  const { isMobile, isDesktop } = useWindowSize();

  const {
    authorization_info: {
      id: customerId,
      email: customerEmail,
    },
  } = useSelector(store => store[STORE_NAMES.AUTH]);
  const {
    availableProducts: {
      subscriptions,
    },
  } = useSelector(store => store[STORE_NAMES.PRODUCTS]);

  const [selectedSubscription, setSelectedSubscription] = useState({});

  const isViewItemTracked = useRef(false);

  useEffect(() => {
    if (selectedSubscription?.id && !isViewItemTracked.current) {
      trackGTMViewItem({
        subscription: selectedSubscription,
        customer: { id: customerId, email: customerEmail },
      });

      isViewItemTracked.current = true;
    }
  }, [selectedSubscription]);

  useEffect(() => {
    if (isDesktop) {
      setPageState(PAGE_STATE.ALL);
    } else {
      setPageState(PAGE_STATE.SUBSCRIPTION_INFO);
    }
  }, [isDesktop]);

  useEffect(() => {
    if (subscriptions.length === 0) {
      dispatch(getAllSubscriptions());
    }
  }, []);

  useEffect(async () => {
    if (subscriptions.length === 0) {
      await dispatch(getAllSubscriptions());
    } else {
      const foundSubscription = getSubscription(id, subscriptions);

      if (!foundSubscription) {
        history.push(routes.notFound);
      }

      setSelectedSubscription(foundSubscription);
    }
  }, [subscriptions]);

  useEffect(() => () => {
    // reset state when leave this screen
    dispatch(resetUserPaymentFlowState());
    dispatch(resetUserActiveOrderState());
  }, []);

  useEffect(() => {
    if (!selectedSubscription) return;

    document.title = formatSubscriptionTitle(selectedSubscription, i18n.language);
  }, [selectedSubscription]);

  const handleRedirectToCheckout = () => {
    setPageState(PAGE_STATE.CHECKOUT);
  };

  const handleRedirectToSubscriptions = () => {
    if (!isDesktop && isBlockVisible(PAGE_STATE.CHECKOUT)) {
      setPageState(PAGE_STATE.SUBSCRIPTION_INFO);
      return;
    }

    history.push(routes.subscriptions);
  };

  const isBlockVisible = state => pageState === state || pageState === PAGE_STATE.ALL;
  const isCheckoutButtonVisible = !isDesktop && isBlockVisible(PAGE_STATE.SUBSCRIPTION_INFO);

  return (
    <Flex {...styles.wrapper}>
      {isDesktop && <Header />}
      <Flex
        {...getContentContainerStyles({ isDesktop, isMobile })}
      >
        <GoBackButton
          onRedirect={handleRedirectToSubscriptions}
          styles={getGoBackButtonStyles({ isDesktop })}
          title={t('GetSubscription.Title')}
        />
        <Flex column={!isDesktop} {...styles.subscriptionInfoAndCheckoutContainer}>
          <Flex {...getContainerStyles({ isOpen: isBlockVisible(PAGE_STATE.SUBSCRIPTION_INFO), isDesktop })}>
            <SubscriptionBlock subscription={selectedSubscription} />
          </Flex>
          <Flex
            {...getContainerStyles({ isDesktop, isOpen: isBlockVisible(PAGE_STATE.CHECKOUT) })}
          >
            <CheckoutBlock
              subscription={selectedSubscription}
            />
          </Flex>
          {isCheckoutButtonVisible && (
            <Button
              {...styles.checkoutButton}
              onClick={handleRedirectToCheckout}
            >
              {t('SubscriptionsInfo.Button')}
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default Subscription;
